import type { AccountFlag } from '@/types/models';
import type { IIFRow } from '@/utils/iif-parser';
import { parseNameFlags } from '@/utils/parse-name-flags';

export interface BuildIifCustomerRowParams {
	cust: IIFRow;
	disambiguatedAccountNumber?: string;
}

export interface IifCustomerRow {
	accountNumber?: string;
	/** The number parsed from NAME, set only when disambiguation suffixed it ("742PS" for "742PS-1"). */
	baseAccountNumber?: string;
	/**
	 * The unit address, from SADDR — never the billing block as a whole (see
	 * `addressMissing`), only a bill-to street for this account's own house number.
	 */
	address?: string;
	/**
	 * No unit address: SADDR was empty and bill-to has no street for this house number.
	 * The billing block is NOT a stand-in: for builder-owned units it is the builder's
	 * HQ, and copying it here once gave 199 Epcon units the same Dublin office as their
	 * property address (#635).
	 */
	addressMissing: boolean;
	/** The address came from a bill-to street for this account's own house number (SADDR empty). */
	addressFromBillTo: boolean;
	/**
	 * The whole billing block as printed — c/o and Attn lines, street, "City, ST ZIP" — one
	 * line per row. Set only when it differs from the physical (e.g. a PO Box). A leading
	 * addressee line that isn't the owner's own name ("Epcon Communities") becomes its c/o line.
	 */
	mailingAddress?: string;
	/** An explicit "c/o X" with no mailing street — printed over the unit address instead. */
	mailingCareOf?: string;
	archivedAt?: number | null;
	externalName?: string;
	externalId?: string;
	flags?: AccountFlag[];
	firstName?: string;
	lastName?: string;
	email?: string;
	phone?: string;
	notes?: string;
	personExternalId?: string;
	missingAccountNumber: boolean;
	closed: boolean;
	/** Business date (UTC midnight) from the close marker — only when it carries a FULL date. */
	closedOn?: number;
	rawName: string;
}

// Matches "(CLOSED)", the successor-pointer variant "(CLOSED, 4825PC)", the
// abbreviated close-date variant "(CLSD4/21)", the marker anywhere in the parens
// ("(NoPymts/CLOSED)" — Hunter's Crossing), and the truncated form with no opening
// paren ("6154BA - CLSD) Epcon Communities"). CLOSED/CLSD must stand alone as a word
// (digits may follow: "CLSD4/21"). Non-global so `.test()` is stateless. Exported so
// the lists-import disambiguation grouping shares ONE definition — a narrower copy
// there once missed the comma variant and mis-numbered the active twin (Avery
// 4821PC → 4821PC-1, Jun 2026).
export const CLOSED_RE
	= /\([^()]*(?<![A-Za-z])(?:CLOSED|CLSD)(?![A-Za-z])[^()]*\)|(?<![A-Za-z(])(?:CLOSED|CLSD)(?![A-Za-z])[^()]*\)/i;
const CLOSED_RE_ALL = new RegExp(CLOSED_RE.source, 'gi');

export interface ParsedCustomerName {
	accountNumber?: string;
	remainder: string;
	closed: boolean;
}

/**
 * Split a flag-stripped QuickBooks customer NAME ("742PS - Doe / Roe") into its
 * unit/account number and the owner remainder. The close marker is removed BEFORE
 * matching because operators put it on either side of the dash —
 * "2124DD (CLSD4/21) - Epcon- Mulberry" has no `NUM - Name` shape until it goes.
 *
 * The ONE parser for both the row builder and the lists-import disambiguation, so
 * the two can never disagree about which rows share a number or which are closed.
 */
export const parseCustomerName = (name: string): ParsedCustomerName => {
	const trimmed = name.trim();
	const closed = CLOSED_RE.test(trimmed);
	const unmarked = trimmed.replace(CLOSED_RE_ALL, ' ').replace(/\s{2,}/g, ' ').trim();
	const m = unmarked.match(/^(\S+)\s*-\s*(.+)$/);
	if (!m) return { remainder: unmarked, closed };
	const head = m[1];
	let tail = m[2];
	// QB annotations like (t-2023)/(tr-2024) leak through parseNameFlags since they aren't known flag codes.
	tail = tail.replace(/\(t[^)]*\)/gi, '').replace(/\(tr[^)]*\)/gi, '').trim();
	tail = tail.replace(/^[(t)]\s*/i, '').trim();
	// The tail of a garbled marker — "(CLOSED) 4/8) Epcon" leaves "4/8)" behind.
	tail = tail.replace(/^[\d/ ]+\)\s*/, '').trim();
	return { accountNumber: head, remainder: tail, closed };
};

/**
 * The close date inside a "(CLOSED …)" / "(CLSD …)" marker, as a business date
 * (UTC midnight ms). Only a FULL m/d/yyyy or m/d/yy date counts: "(CLSD4/21)" is
 * either April 21 of an unstated year or April 2021, and guessing either one would
 * write a confident-looking date nobody entered.
 */
export const parseClosedOn = (name: string): number | undefined => {
	const marker = name.match(CLOSED_RE)?.[0];
	const m = marker?.match(/(\d{1,2})\/(\d{1,2})\/(\d{4}|\d{2})(?!\d)/);
	if (!m) return undefined;
	const month = Number(m[1]);
	const day = Number(m[2]);
	const year = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
	const ms = Date.UTC(year, month - 1, day);
	const d = new Date(ms);
	if (d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return undefined;
	return ms;
};

export interface PostalParts {
	streetLines: string[];
	city?: string;
	state?: string;
	zip?: string;
}

// "Dublin, OH 43017" / "Dublin OH 43017-1234". The city may not contain digits, so a
// one-line "PO Box 364, Galloway OH 43119" stays whole rather than becoming a city.
const CITY_STATE_ZIP_RE = /^([A-Za-z][A-Za-z .'-]*?),?\s+([A-Za-z]{2})\.?\s+(\d{5}(?:-\d{4})?)$/;

/**
 * Split an address block's trailing "City, ST 12345" line into its parts. A block
 * whose last line doesn't parse — or that has no street line above it — is
 * returned whole, with no parts.
 */
export const splitCityStateZip = (lines: string[]): PostalParts => {
	const m = lines.length >= 2 ? lines[lines.length - 1].match(CITY_STATE_ZIP_RE) : null;
	if (!m) return { streetLines: lines };
	return { streetLines: lines.slice(0, -1), city: m[1].trim(), state: m[2].toUpperCase(), zip: m[3] };
};

const joinAddress =(raw: Record<string, string>, prefix: 'SADDR' | 'BADDR'): string | undefined => {
	const parts = [raw[`${prefix}1`], raw[`${prefix}2`], raw[`${prefix}3`], raw[`${prefix}4`], raw[`${prefix}5`]]
		.map((s) => (s || '').trim())
		.filter(Boolean);
	if (!parts.length) return undefined;
	return parts.join(', ').replace(/"/g, '');
};

/**
 * Cleaned address lines for one QB block: strip surrounding quotes and the ACH
 * backtick, collapse runs of whitespace, drop "(re: …)" reference annotations
 * QB operators tuck into the billing block, and drop empty lines.
 */
const addrLines = (raw: Record<string, string>, prefix: 'SADDR' | 'BADDR'): string[] =>
	[1, 2, 3, 4, 5]
		.map((i) => (raw[`${prefix}${i}`] || '').replace(/["`]/g, '').replace(/\s{2,}/g, ' ').trim())
		.filter((l) => l.length > 0 && !/^\(?\s*re:/i.test(l));

/**
 * Drop a leading QB "label" line from an address block — SADDR1 is often the
 * community/property NAME ("Bennington Cove condo"), BADDR1 the addressee NAME
 * ("Sarah B Hoar") — neither a street. When the first line has no street number
 * and a later line does, drop it so the address starts at the street.
 */
const dropLabelLine = (lines: string[]): string[] => {
	if (lines.length >= 2 && !/\d/.test(lines[0]) && lines.slice(1).some((l) => /\d/.test(l))) {
		return lines.slice(1);
	}
	return lines;
};

/** A street line: "4734 Vine Lane", "500Stonehenge Parkway", "PO Box 9" — not "5977AS" or "(Edison4802)". */
const isStreetLine = (l: string): boolean =>
	/^\d+\s+[A-Za-z]/.test(l) || /^\d+[A-Za-z]{5,}/.test(l) || /^p\.?\s*o\.?\s*box\b/i.test(l);

/** A line that routes the mail rather than locating it: "c/o Kairos Living LLC", "Attn: FIS - HOA DEPT." */
const ROUTING_RE = /^(?:c\/o\s+\S|attn\b)/i;

/** "c/o Cannon LLC, re: SandCt5086)" → "c/o Cannon LLC" — the office's unit reference rides on the c/o line. */
const stripReference = (l: string): string => l.replace(/[,;]?\s*\(?\s*\bre:.*$/i, '').trim();

interface BillTo {
	/**
	 * The mailing lines, in block order: every c/o / Attn line plus the first street line
	 * on (city line included). Printed verbatim, so nothing deliverable may be left out.
	 */
	mailing: string[];
	/** Whether `mailing` carries a c/o or Attn line of its own. */
	routed: boolean;
	/** The first dropped line above the street — a possible addressee / c/o. */
	label?: string;
	/** An explicit "c/o X" with no mailing street to print it over — the unit address gets it. */
	careOf?: string;
	/** The unit's own street (+ city line) found in bill-to — used only when ship-to is empty. */
	unit?: string[];
}

/**
 * Take apart a QB bill-to block as the office actually fills it in (shapes from the
 * Courtyards and Hickory Grove IIF exports): a label line ("Epcon-Carr Farms", an
 * addressee), junk the office types for its own reference ("(Edison4802)", "5977AS",
 * "2127CD - Epcon-Mulberry"), sometimes a "c/o X" or "Attn: X" line, sometimes the
 * unit's own street, then the mailing street and city line.
 */
const splitBillTo = (lines: string[], house: string | undefined, shipToEmpty: boolean): BillTo => {
	const routing = new Set(lines.flatMap((l, i) => (ROUTING_RE.test(l) ? [i] : [])));
	const careIdx = [...routing].find((i) => /^c\/o/i.test(lines[i])) ?? -1;
	const careOf = careIdx >= 0 ? stripReference(lines[careIdx]).replace(/^c\/o\s+/i, '') || undefined : undefined;
	const ownRe = house ? new RegExp(`^${house}\\s+[A-Za-z]`) : null;
	const own = ownRe ? lines.findIndex((l, i) => !routing.has(i) && ownRe.test(l)) : -1;
	const other = lines.findIndex((l, i) => !routing.has(i) && i !== own && isStreetLine(l));
	const used = new Set<number>(routing);
	let unit: string[] | undefined;
	// The unit's own street is the PROPERTY only when ship-to is empty and either nothing
	// else is in bill-to or another (mailing) street follows it.
	if (own >= 0 && shipToEmpty) {
		unit = [lines[own]];
		used.add(own);
		const next = lines[own + 1];
		// A city line directly under the unit's street is the UNIT's — even when the
		// builder's mailing street follows it (Codex P2).
		if (next && CITY_STATE_ZIP_RE.test(next)) {
			unit.push(next);
			used.add(own + 1);
		}
	}
	const start = other >= 0 ? other : own >= 0 && !used.has(own) ? own : -1;
	if (start < 0) {
		const rest = lines.filter((_, i) => !used.has(i));
		if (unit) return { mailing: [], routed: false, careOf, unit };
		const mailing = dropLabelLine(rest);
		return { mailing, routed: false, careOf, label: mailing.length < rest.length ? rest[0] : undefined };
	}
	return {
		mailing: lines.flatMap((l, i) => (routing.has(i) ? [stripReference(l)] : i >= start && !used.has(i) ? [l] : []))
			.filter(Boolean),
		routed: routing.size > 0,
		label: lines.find((_, i) => i < start && !used.has(i)),
		unit,
	};
};

/** Normalize for "same address?" comparison — case/space/punctuation-insensitive. */
const normAddr = (lines: string[]): string => lines.join(' ').toLowerCase().replace(/[^a-z0-9]/g, '');

export const buildIifCustomerRow = ({
	cust,
	disambiguatedAccountNumber,
}: BuildIifCustomerRowParams): IifCustomerRow | null => {
	const raw = cust.raw;
	const rawName = (raw.NAME || '').trim();
	if (!rawName) return null;

	// Strip flag tokens first or they leak into the firstName fallback split.
	const nameParsed = parseNameFlags(rawName);
	const cleanedRawName = nameParsed.name;
	const flagSet = new Set<AccountFlag>(nameParsed.flags);

	const { accountNumber: parsedNum, remainder, closed } = parseCustomerName(cleanedRawName);
	const hidden = (raw.HIDDEN || '').toUpperCase() === 'Y' || closed;
	const accountNumber = disambiguatedAccountNumber ?? parsedNum;

	// Some operators put `(r)` in BADDR1 instead of NAME.
	const rawShipping = joinAddress(raw, 'SADDR');
	const rawBilling = joinAddress(raw, 'BADDR');
	const shippingParsed = rawShipping ? parseNameFlags(rawShipping) : null;
	const billingParsed = rawBilling ? parseNameFlags(rawBilling) : null;
	for (const f of shippingParsed?.flags ?? []) flagSet.add(f);
	for (const f of billingParsed?.flags ?? []) flagSet.add(f);

	// Physical (unit) address from SADDR; drop the QB line-1 community/property label so
	// it starts at the street. The bill-to block is NOT a stand-in — for builder-owned
	// units it is the builder's office — with one exception: a bill-to street for this
	// account's OWN house number ("3284 Natal Ln" above the builder HQ) is the unit.
	const shippingLines = addrLines(raw, 'SADDR');
	const billingLines = addrLines(raw, 'BADDR');
	const house = parsedNum?.match(/^(\d+)/)?.[1];
	const bill = splitBillTo(billingLines, house, shippingLines.length === 0);
	const physicalLines = shippingLines.length ? dropLabelLine(shippingLines) : (bill.unit ?? []);
	// Newline-joined (one QB line per row) so the address renders multi-line in
	// the edit form and splits cleanly on envelopes/statements — same as mailing.
	const address = physicalLines.length
		? parseNameFlags(physicalLines.join('\n')).name.trim() || undefined
		: undefined;

	// Mailing address from BADDR, from its first street line on (labels and junk above
	// it dropped). ONLY set it when it genuinely DIFFERS from the physical address — e.g.
	// an owner who bills to a PO Box ("Sarah B Hoar … PO Box 364"). When BADDR is just
	// the owner name over the SAME street as SADDR (the common case), leave
	// mailing_address NULL so statements/envelopes fall back to the physical address.
	//
	// It is ONE text field — c/o and Attn lines, street, "City, ST ZIP" — because that
	// field is what the envelope, statement and invoice print. Splitting the city line or
	// the c/o into side columns once left Hickory Grove invoices with a bare street.
	// A bill-to that is the unit's own street with no city line of its own (bare, or with the
	// office's notes typed under it) is the unit too: stored as mailing it would print with
	// no "City, ST ZIP" at all, where the unit address prints a deliverable one. Matched on the
	// house number, since the office spells the street differently in the two blocks.
	const unitStreetOnly = !!house && physicalLines.length > 0 && !bill.routed
		&& !bill.mailing.some((l) => CITY_STATE_ZIP_RE.test(l)) && new RegExp(`^${house}\\s`).test(bill.mailing[0] ?? '');
	const mailing = bill.mailing.length && !unitStreetOnly && normAddr(bill.mailing) !== normAddr(physicalLines)
		? bill.mailing
		: undefined;
	// Without a c/o / Attn line of its own, the dropped addressee line is the c/o — unless
	// it names the owner, which would print "c/o Jane Doe & John Roe" under "Jane Doe", or
	// is junk ("(Edison4802)", "5977AS", "2127CD - Epcon-Mulberry"). Any shared word with the
	// customer's own name counts as "names the owner"; a missed c/o is the safe miss.
	const labelLine = mailing && !bill.routed && bill.label ? parseNameFlags(bill.label).name.trim() : '';
	const words = (s: string): string[] => s.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length >= 3);
	const ownerWords = new Set(words(`${raw.LASTNAME || ''} ${remainder}`));
	const labelIsCareOf = /^[A-Za-z][^\d()]*$/.test(labelLine) && !words(labelLine).some((w) => ownerWords.has(w));
	const mailingText = mailing ? [...(labelIsCareOf ? [`c/o ${labelLine}`] : []), ...mailing].join('\n') : undefined;

	const firstNameRaw = (raw.FIRSTNAME || '').trim();
	const midInit = (raw.MIDINIT || '').trim();
	const lastNameRaw = (raw.LASTNAME || '').trim();
	let firstName = firstNameRaw;
	let lastName: string | undefined = [midInit, lastNameRaw].filter(Boolean).join(' ') || undefined;
	if (!firstName) {
		const fb = remainder.split(/\s+/).filter(Boolean);
		firstName = fb[0] || cleanedRawName;
		lastName = fb.slice(1).join(' ') || undefined;
	}

	const personNotes = [raw.NOTE, raw.NOTEPAD].map((s) => (s || '').trim()).filter(Boolean).join('\n\n') || undefined;
	const externalId = (raw.REFNUM || '').trim() || undefined;
	const flags = [...flagSet];

	return {
		accountNumber: accountNumber || undefined,
		baseAccountNumber: parsedNum && accountNumber && parsedNum !== accountNumber ? parsedNum : undefined,
		address,
		addressMissing: !address,
		addressFromBillTo: !!address && shippingLines.length === 0,
		mailingAddress: mailingText,
		mailingCareOf: bill.careOf,
		archivedAt: hidden ? Date.now() : undefined,
		externalName: cleanedRawName,
		externalId,
		flags: flags.length > 0 ? flags : undefined,
		firstName,
		lastName,
		email: (raw.EMAIL || '').trim() || undefined,
		phone: (raw.PHONE1 || raw.PHONE || '').trim() || undefined,
		notes: personNotes,
		personExternalId: externalId,
		missingAccountNumber: !accountNumber,
		closed,
		closedOn: closed ? parseClosedOn(cleanedRawName) : undefined,
		rawName,
	};
};
